import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCart } from "./CartContext";
import config from "../config";
import "../styles/success.css";

import { FaHeart, FaRegCircleCheck } from "react-icons/fa6";
import { LuPackage } from "react-icons/lu";
import { FiCalendar } from "react-icons/fi";
import { FaRegEnvelope } from "react-icons/fa6";
import { FaRegHeart } from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";

const host = config.backendUrl;

const OrderPage = ({title}) => {

    /* checkout session ID is in the URL */ 
    const [searchParams] = useSearchParams();
    const trackingToken = searchParams.get("tracking_token");

    /* fetching order details from DB using session ID */
    const [orderDetails, setOrderDetails] = useState(null);
    const [shippedItems, setShippedItems] = useState([]);

    /* on load, clear the cart and fetch order details */
    useEffect(() => {
        document.title = title;
        if (trackingToken) {
            fetch(`${host}/api/order?tracking_token=${trackingToken}`, {
                method: "GET",
                headers: {
                    "X-API-Key": process.env.REACT_APP_GANDF_KEY,
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    console.log("Fetched order data:", data);
                    setOrderDetails(data);
                    for (const item of data.items) {
                        if (item.status === "shipped" || item.status === "delivered") {
                            setShippedItems((prev) => [...prev, item]);
                        }
                    }
                })
                .catch((err) => console.error("Error fetching order:", err));
        }
    }, [trackingToken]);

    /* convert cents to dollars and round */
    const formatPrice = (price) => {
        price = price / 100;
        const options = {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        };
        return new Intl.NumberFormat("en-US", options).format(price);
    }

    /* convert timestamp to readable date */
    const formatDate = (date) => {
        console.log("formatDate called with:", date);
        if (typeof date === "number") {
            date = date * 10;
            console.log("Date is a number:", date);
        } else if (typeof date === "string") {
            date = new Date(date).getTime();
        }
        const options = {
            year: "numeric",
            month: "long",
            day: "numeric",
        };
        return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
    }

    let currentStep = 0;
    if (orderDetails && orderDetails.status) {
        switch (orderDetails.status) {
            case undefined || "created" || "unpaid" || "in_production" || "production_delay" || "order_placed" || "sent_to_printer":
                currentStep = 0;
                if (orderDetails.items.any(item => item.status === "shipped")) {
                    currentStep = 1; // If any item is shipped, move to shipped step
                }
                break;
            case "shipped":
                currentStep = 2;
                break;
            case "in_production":
                currentStep = 1; // In production is considered as shipped for progress
                break;
            case "delivered":
                currentStep = 3; 
                break;
            default:
                currentStep = 0; // Default to order placed if status is unknown
        }
    }

    const progressFillRef = useRef(null);
    const circleRefs = [useRef(null), useRef(null), useRef(null),  useRef(null)];

    useEffect(() => {
        if (orderDetails && progressFillRef.current && !orderDetails.promotion_code) {
            const progressLevels = [20, 45, 75, 100];
            const progressPercent = progressLevels[currentStep];
            progressFillRef.current.style.width = progressPercent + '%';

            // Update circles
            circleRefs.forEach((circleRef, index) => {
                if (circleRef.current) {
                    if (index <= currentStep) {
                        circleRef.current.classList.add('filled');
                    } else {
                        circleRef.current.classList.remove('filled');
                    }
                }
            });
        }
    }, [orderDetails, currentStep]); // Re-run when orderDetails or currentStep changes

    return (
        <div className="success">
            <div className="success-header">
                <LuPackage className="success-icon" />
                {orderDetails && orderDetails.promotion_code && (
                    <span className="success-title">Gift sent by {orderDetails.items[0].sender_name}</span>
                )}
                {orderDetails && !orderDetails.promotion_code && (
                    <span className="success-title">Your Order Status</span>
                )}
            </div>

            {orderDetails && !orderDetails.promotion_code && (
                <div className="progress-container">
                    <div className="progress-wrapper">
                        <div className="progress-line">
                            <div className="progress-fill" id="progressFill" ref={progressFillRef}></div>
                        </div>
                    <div className="progress-circles">
                        <div className="progress-circle filled" id="circle1" ref={circleRefs[0]}>
                            {currentStep >= 0 && <span>&#x2713;</span>}
                        </div>
                        <div className="progress-circle" id="circle2" ref={circleRefs[1]}>
                            {currentStep >= 1 && <span>&#x2713;</span>}
                        </div>
                        <div className="progress-circle" id="circle3" ref={circleRefs[2]}>
                            {currentStep >= 2 && <span>&#x2713;</span>}
                        </div>
                        <div className="progress-circle" id="circle4" ref={circleRefs[3]}>
                            {currentStep >= 3 && <span>&#x2713;</span>}
                        </div>
                    </div>
                </div>

                <div className="progress-labels">
                    <div className="progress-label">Order Placed</div>
                    <div className="progress-label">In Production</div>
                    <div className="progress-label">Shipped</div>
                    <div className="progress-label">Delivered</div>
                </div>
            </div>

            )}

            {orderDetails ? (
                <div className="order-details">
                    <div className="order-summary">

                        {shippedItems.length > 0 && !orderDetails.promotion_code && (
                            <div className="tracking-container">
                                <div className="tracking">
                                    <div className="tracking-text">
                                        <span className="tracking-title">Track with {shippedItems[0]["tracking"]["carrier_name"]}</span>
                                        <span className="tracking-subtitle">Get real-time updates on your shipment.</span>
                                    </div>
                                    <a href={shippedItems[0]["tracking"]["tracking_url"]} target="_blank" rel="noopener noreferrer" className="tracking-link">
                                        <span className="tracking-link-text">Track Your Order</span>
                                        <FaExternalLinkAlt className="tracking-link-icon" />
                                    </a>
                                </div>
                            </div>
                        )}

                        <span className="order-summary-title">Order Summary</span>

                        <div className="order-summary-items">
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Order ID</span>
                                <span className="order-summary-item-value">{orderDetails.id}</span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Order Date</span>
                                <span className="order-summary-item-value">{formatDate(orderDetails.timestamp * 100)}</span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Name</span>
                                <span className="order-summary-item-value">
                                    {orderDetails.billing.name}
                                </span>
                            </div>
                            <div className="order-summary-item">
                                <span className="order-summary-item-label">Email</span>
                                <span className="order-summary-item-value">{orderDetails.billing.email}</span>
                            </div>
                        </div>

                        <span className="order-items-title">Items</span>
                        {orderDetails.items.map((item) => (
                            <div key={item.id} className="order-item">
                                <img src={item.image} alt={item.description} className={`${item.recipient ? "order-item-gift-image" : "order-item-image"}`} />
                                <div className="order-item-details">
                                    <span className="order-item-title">{item.name}</span>
                                    {item.recipient ? (
                                        <span className="order-item-recipient">
                                            Sent to {item.recipient}
                                        </span>
                                        ) : (
                                        <span className="order-item-recipient">{item.variation.recipient}</span>
                                    )}
                                    <span className="order-item-quantity">Quantity: {item.quantity}</span>
                                    <span className="order-item-value">${(item.price / 100).toFixed(2)}</span>
                                </div>
                            </div>
                        ))}

                        <hr className="order-summary-divider" />

                        <div className="order-total">
                            <div className="order-total-text">
                                <p>Subtotal</p>
                                <p>{formatPrice(orderDetails.cost.subtotal)}</p>
                            </div>

                            {!orderDetails.promotion_code && (
                                <>
                                    <div className="order-total-text">
                                        <p>Shipping</p>
                                        <p>{formatPrice(orderDetails.cost.shipping)}</p>
                                    </div>
                                    <div className="order-total-text">
                                        <p>Tax</p>
                                        <p>{formatPrice(orderDetails.cost.tax)}</p>
                                    </div>
                                </>
                            )}

                            {orderDetails.discount && (
                                <div className="order-total-text">
                                    <p>Discount</p>
                                    <p>-{formatPrice(orderDetails.cost.discount)}</p>
                                </div>
                            )}

                            <hr className="order-summary-divider" />

                            <div className="order-total-text">
                                <p><strong>Total</strong></p>
                                <p><strong>{formatPrice(orderDetails.cost.total)}</strong></p>
                            </div>
                        </div> 
                    </div>

                    {!orderDetails.promotion_code && (

                    <div className="order-side">
                        <div className="shipping-info">
                            <div className="shipping-info-title">
                                <LuPackage className="shipping-info-icon" />
                                <span className="shipping-info-title-text">Shipping Information</span>
                            </div>
                            <div className="shipping-info-items">
                                <span className="shipping-info-value">{orderDetails.shipping.name}</span>
                                <span className="shipping-info-value">{orderDetails.shipping.address.line1}</span>
                                {orderDetails.shipping.address.line2 && <span className="shipping-info-value">{orderDetails.shipping.address.line2}</span>}
                                <span className="shipping-info-value">{orderDetails.shipping.address.city}, {orderDetails.shipping.address.state} {orderDetails.shipping.address.postal_code}</span>
                                <span className="shipping-info-value">{orderDetails.shipping.address.country}</span>
                            </div>

                            <hr className="order-summary-divider" />

                            <div className="estimated-delivery">
                                <span className="estimated-delivery-title">Estimated Delivery</span>
                                    <div className="estimated-delivery-date">
                                        <FiCalendar className="estimated-delivery-icon" />
                                        {formatDate(orderDetails.shipping.estimated_delivery.max_delivery_date)}
                                    </div>
                            </div>
                        </div>

                        <div className="whats-next">
                            <span className="whats-next-title">What's Next?</span>
                            <div className="whats-next-text">
                                <div className="whats-next-text-item">
                                    <FaRegEnvelope className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">You will receive an email confirmation shortly.</span>
                                </div>
                                <div className="whats-next-text-item">
                                    <LuPackage className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">We'll notify you when your order ships.</span>
                                </div>
                                <div className="whats-next-text-item">
                                    <FaRegHeart className="whats-next-text-icon" />
                                    <span className="whats-next-text-description">If you have any issues with your order, don't hesitate to reach out to us.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    {orderDetails.promotion_code && (
                        <div className="order-side">
                             <div className="whats-next-redemption">
                                <span className="whats-next-title">How to Redeem Your Gift</span>
                                <div className="whats-next-text">
                                    <div className="whats-next-text-item">
                                        <FaRegEnvelope className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">We’ve emailed your recipient a gift code they can use at checkout.</span>
                                    </div>
                                    <div className="whats-next-text-item">
                                        <LuPackage className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">Your gift recipient can now personalize their book and redeem your gift code whenever it feels right.</span>
                                    </div>
                                    <div className="whats-next-text-item">
                                        <FaRegHeart className="whats-next-text-icon" />
                                        <span className="whats-next-text-description">If you have any issues with your order, don’t hesitate to reach out to us!</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <p>Loading order details...</p>
            )}
        </div>
    );
};

export default OrderPage;
